import {computed, ref, type ComputedRef, type Ref} from 'vue';
import type {ReccoTopicDTO} from '@/ts/types/dto/recco/topic.dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import type {ExploreLibraryFilters, LibraryContentTypeDTO, LibraryMediaTypeDTO} from '@/ts/types/dto/recco/library.dto';

function toggleValue<T>(list: Ref<T[]>, value: T): void {
  list.value = list.value.includes(value)
    ? list.value.filter((entry) => entry !== value)
    : [...list.value, value];
}

/** Reactive facet selection and derived state returned by {@link useExploreFilters}. */
export interface UseExploreFilters {
  mediaTypes: Ref<LibraryMediaTypeDTO[]>;
  topics: Ref<ReccoTopicDTO[]>;
  contentTypes: Ref<LibraryContentTypeDTO[]>;
  saved: Ref<boolean>;
  partnerIds: Ref<UuidDTO[]>;
  /** True when at least one facet is active — drives the switch to the filtered view. */
  isActive: ComputedRef<boolean>;
  /** True when "Saved" is the only active facet, so an empty result is about bookmarks. */
  isSavedOnly: ComputedRef<boolean>;
  /** Number of active selections — shown as the "Filters (n)" count. */
  activeCount: ComputedRef<number>;
  filters: ComputedRef<ExploreLibraryFilters>;
  toggleMediaType(value: LibraryMediaTypeDTO): void;
  toggleTopic(value: ReccoTopicDTO): void;
  toggleContentType(value: LibraryContentTypeDTO): void;
  toggleSaved(): void;
  togglePartner(id: UuidDTO): void;
  clearAll(): void;
}

/**
 * Holds the Explore filter-bar facet selection: OR within each facet, AND across facets
 * (resolved by the BE). State is page-scoped and intentionally NOT persisted — it resets on
 * reload (no URL state), per IPD-2935 §2.
 */
export function useExploreFilters(): UseExploreFilters {
  const mediaTypes = ref<LibraryMediaTypeDTO[]>([]);
  const topics = ref<ReccoTopicDTO[]>([]);
  const contentTypes = ref<LibraryContentTypeDTO[]>([]);
  const saved = ref<boolean>(false);
  const partnerIds = ref<UuidDTO[]>([]);

  const activeCount = computed<number>(() =>
    mediaTypes.value.length +
    topics.value.length +
    contentTypes.value.length +
    (saved.value ? 1 : 0) +
    partnerIds.value.length);

  const isActive = computed<boolean>(() => activeCount.value > 0);

  const isSavedOnly = computed<boolean>(() => saved.value && activeCount.value === 1);

  const filters = computed<ExploreLibraryFilters>(() => ({
    mediaTypes: mediaTypes.value,
    topics: topics.value,
    contentTypes: contentTypes.value,
    saved: saved.value,
    // Part of the endpoint's facet set, but nothing sets it today: there is no For-you toggle in
    // the filter bar and no page asks for the recommended subset. Sent as false so the request
    // shape matches the contract; the moment something needs it, this is where it hangs.
    recommended: false,
    partnerIds: partnerIds.value,
  }));

  return {
    mediaTypes,
    topics,
    contentTypes,
    saved,
    partnerIds,
    isActive,
    isSavedOnly,
    activeCount,
    filters,
    toggleMediaType: (value): void => { toggleValue(mediaTypes, value); },
    toggleTopic: (value): void => { toggleValue(topics, value); },
    toggleContentType: (value): void => { toggleValue(contentTypes, value); },
    toggleSaved: (): void => {
      saved.value = !saved.value;
    },
    togglePartner: (id): void => { toggleValue(partnerIds, id); },
    clearAll: (): void => {
      mediaTypes.value = [];
      topics.value = [];
      contentTypes.value = [];
      saved.value = false;
      partnerIds.value = [];
    },
  };
}
